import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Banknote, Download, Loader2, Plus, Search, Wallet } from 'lucide-react';
import { YourCheckaNavigation } from '@/components/Navbar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import AddBankAccountModal from '@/components/modals/AddBankAccountModal';
import { useAppSelector } from '@/hooks/useAppStore';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { useGetHrEarningsQuery, useGetHrEarningsSummaryQuery, useGetMyBankAccountsQuery } from '@/store/api/apiSlice';
import type { HrEarning, HrEarningStatus } from '@/types';

function formatCurrency(amount: number | undefined) {
    if (amount === undefined || amount === null || isNaN(amount)) return '₦0';
    return new Intl.NumberFormat('en-NG', {
        style: 'currency',
        currency: 'NGN',
        minimumFractionDigits: 0,
    }).format(amount);
}

function getInitials(firstName?: string, lastName?: string) {
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || 'U';
}

type Filter = 'all' | HrEarningStatus;

const STATUS_BADGE: Record<HrEarningStatus, string> = {
    pending: 'bg-amber-50 text-amber-700 border-amber-100',
    paid: 'bg-green-50 text-green-700 border-green-100',
    voided: 'bg-gray-100 text-gray-600 border-gray-200',
};

export default function HrEarnings() {
    const navigate = useNavigate();
    const currentUser = useAppSelector(selectCurrentUser);

    const [filter, setFilter] = useState<Filter>('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [isExporting, setIsExporting] = useState(false);
    const [exportError, setExportError] = useState<string | null>(null);
    const [isAddBankOpen, setIsAddBankOpen] = useState(false);

    const statusParam = filter === 'all' ? undefined : filter;
    const { data: earnings = [], isLoading } = useGetHrEarningsQuery(statusParam ? { status: statusParam } : undefined);
    const { data: summary } = useGetHrEarningsSummaryQuery();
    const { data: bankAccounts = [], isLoading: bankAccountsLoading } = useGetMyBankAccountsQuery();
    const hasBankAccount = bankAccounts.length > 0;

    const filtered = useMemo(() => {
        if (!searchQuery.trim()) return earnings;
        const q = searchQuery.toLowerCase();
        return earnings.filter((e) => {
            const company = e.employer?.companyName?.toLowerCase() ?? '';
            const employerName = `${e.employer?.firstName ?? ''} ${e.employer?.lastName ?? ''}`.toLowerCase();
            return (
                e.employeeName.toLowerCase().includes(q) ||
                company.includes(q) ||
                employerName.includes(q) ||
                e.checkType.toLowerCase().includes(q)
            );
        });
    }, [earnings, searchQuery]);

    const handleExport = async () => {
        setExportError(null);
        setIsExporting(true);
        try {
            const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1';
            const token = localStorage.getItem('access_token');
            const response = await fetch(`${baseUrl}/hr/earnings/export`, {
                method: 'GET',
                headers: {
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
            });
            if (!response.ok) {
                throw new Error(`Export failed (${response.status})`);
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `hr-earnings-${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setExportError(err instanceof Error ? err.message : 'Failed to export CSV');
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <main className="w-full min-h-screen flex flex-col px-4 py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]">
            <div className="bg-white rounded-3xl shadow-sm flex-1 flex flex-col overflow-hidden">
                <YourCheckaNavigation
                    showTitle={false}
                    showInfo={false}
                    showLocation={false}
                    notificationCount={0}
                    userName={currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'HR Partner'}
                    userInitials={currentUser ? getInitials(currentUser.firstName, currentUser.lastName) : 'HR'}
                    only={true}
                    className="border-b border-gray-100"
                />

                <div className="flex-1 overflow-y-auto p-4 md:p-8 lg:p-10 flex flex-col gap-6">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div>
                            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900">Earnings</h1>
                            <p className="text-sm text-gray-500 mt-1">
                                Every commission you've earned across your referred employers.
                            </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <Button
                                onClick={() => setIsAddBankOpen(true)}
                                className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2.5 rounded-xl text-sm font-medium flex items-center gap-2"
                            >
                                {hasBankAccount ? (
                                    <Banknote className="h-4 w-4 text-[#f37b23]" />
                                ) : (
                                    <Plus className="h-4 w-4 text-[#f37b23]" />
                                )}
                                {hasBankAccount ? 'Add another bank' : 'Add bank account'}
                            </Button>
                            <Button
                                onClick={handleExport}
                                disabled={isExporting}
                                className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2.5 rounded-xl text-sm font-medium flex items-center gap-2"
                            >
                                {isExporting ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Download className="h-4 w-4" />
                                )}
                                Export CSV
                            </Button>
                            <Button
                                onClick={() => navigate('/withdrawals')}
                                className="bg-[#f37b23] hover:bg-[#e06a15] text-white px-4 py-2.5 rounded-xl text-sm font-medium flex items-center gap-2"
                            >
                                <Wallet className="h-4 w-4" />
                                Withdraw
                            </Button>
                        </div>
                    </div>

                    {/* No-bank banner — surfaced inline so HR can fix it without leaving the page */}
                    {!bankAccountsLoading && !hasBankAccount && (
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-orange-100 bg-orange-50 px-4 py-3">
                            <div className="flex items-start gap-3">
                            
                                <div>
                                    <p className="text-sm font-medium text-orange-900">
                                        Add a bank account to start withdrawing.
                                    </p>
                                    <p className="text-xs text-orange-800/80 mt-0.5">
                                        You can't request a payout until we know where to send the money.
                                    </p>
                                </div>
                            </div>
                            <Button
                                onClick={() => setIsAddBankOpen(true)}
                                className="self-start sm:self-auto bg-gray-900 hover:bg-gray-800 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
                            >
                                <Plus className="h-4 w-4" />
                                Add bank account
                            </Button>
                        </div>
                    )}

                    {exportError && (
                        <div className="rounded-xl border border-red-100 bg-red-50 text-red-700 text-sm px-4 py-3">
                            {exportError}
                        </div>
                    )}

                    {/* Summary cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                        <div className="bg-gray-50 rounded-2xl p-4 md:p-5">
                            <p className="text-xs text-gray-500 mb-1">Lifetime gross</p>
                            <p className="text-2xl md:text-3xl font-bold text-gray-900">
                                {formatCurrency(summary?.lifetimeGross)}
                            </p>
                            <p className="text-[11px] text-gray-400 mt-1">
                                {summary?.counts?.paid ?? 0} paid · {summary?.counts?.pending ?? 0} pending
                            </p>
                        </div>
                        <div className="bg-green-50 rounded-2xl p-4 md:p-5">
                            <p className="text-xs text-green-600 mb-1">Paid (available)</p>
                            <p className="text-2xl md:text-3xl font-bold text-green-900">
                                {formatCurrency(summary?.paid)}
                            </p>
                            <p className="text-[11px] text-green-700 mt-1">
                                Minimum payout {formatCurrency(summary?.minPayoutAmount)}
                            </p>
                        </div>
                        <div className="bg-amber-50 rounded-2xl p-4 md:p-5">
                            <p className="text-xs text-amber-600 mb-1">Pending</p>
                            <p className="text-2xl md:text-3xl font-bold text-amber-900">
                                {formatCurrency(summary?.pending)}
                            </p>
                            <p className="text-[11px] text-amber-700 mt-1">Pending — clears on report approval</p>
                        </div>
                        <div className="bg-gray-50 rounded-2xl p-4 md:p-5">
                            <p className="text-xs text-gray-500 mb-1">Voided</p>
                            <p className="text-2xl md:text-3xl font-bold text-gray-700">
                                {formatCurrency(summary?.voided)}
                            </p>
                            <p className="text-[11px] text-gray-400 mt-1">
                                {summary?.counts?.voided ?? 0} reversed by admin
                            </p>
                        </div>
                    </div>

                    {/* Filters / search */}
                    <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
                        <div className="relative w-full sm:max-w-xs">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search employer, employee or tier"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-transparent"
                            />
                        </div>
                        <div className="flex items-center gap-2 overflow-x-auto">
                            {(['all', 'pending', 'paid', 'voided'] as Filter[]).map((f) => (
                                <button
                                    key={f}
                                    onClick={() => setFilter(f)}
                                    className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors capitalize ${
                                        filter === f
                                            ? 'bg-gray-900 text-white'
                                            : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                                    }`}
                                >
                                    {f}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Add Bank Account Modal */}
                    <AddBankAccountModal isOpen={isAddBankOpen} onClose={() => setIsAddBankOpen(false)} />

                    {/* Table */}
                    <div className="rounded-2xl border border-gray-100 overflow-hidden">
                        {isLoading ? (
                            <div className="flex items-center justify-center py-16">
                                <Loader2 className="h-6 w-6 text-gray-400 animate-spin" />
                            </div>
                        ) : filtered.length === 0 ? (
                            <div className="flex flex-col items-center justify-center text-center py-12 px-6">
                                <p className="text-gray-500 text-sm">
                                    No earnings found{filter !== 'all' ? ` for "${filter}"` : ''}.
                                </p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-[820px]">
                                    <thead className="bg-gray-50/60">
                                        <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            <th className="px-4 py-3">Employer</th>
                                            <th className="px-4 py-3">Employee</th>
                                            <th className="px-4 py-3">Tier</th>
                                            <th className="px-4 py-3">List price</th>
                                            <th className="px-4 py-3">Commission</th>
                                            <th className="px-4 py-3">Status</th>
                                            <th className="px-4 py-3">Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filtered.map((earning) => (
                                            <EarningRow key={earning.id} earning={earning} />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </main>
    );
}

function EarningRow({ earning }: { earning: HrEarning }) {
    const employerName = earning.employer?.companyName
        ? earning.employer.companyName
        : `${earning.employer?.firstName ?? ''} ${earning.employer?.lastName ?? ''}`.trim() || '—';
    const dateLabel = earning.paidAt || earning.createdAt;
    return (
        <tr className="border-b border-gray-100 hover:bg-gray-50/60 transition-colors">
            <td className="px-4 py-4">
                <p className="text-sm font-medium text-gray-900">{employerName}</p>
                {earning.employer?.companyName &&
                    `${earning.employer?.firstName ?? ''} ${earning.employer?.lastName ?? ''}`.trim() && (
                        <p className="text-xs text-gray-500">
                            {earning.employer.firstName} {earning.employer.lastName}
                        </p>
                    )}
            </td>
            <td className="px-4 py-4 text-sm text-gray-800">{earning.employeeName}</td>
            <td className="px-4 py-4">
                <Badge
                    variant="outline"
                    className="bg-indigo-50 text-indigo-600 border-indigo-100 px-3 py-1 rounded-lg font-medium capitalize"
                >
                    {earning.checkType}
                </Badge>
            </td>
            <td className="px-4 py-4 text-sm text-gray-700">{formatCurrency(earning.listPrice)}</td>
            <td className="px-4 py-4 text-sm font-semibold text-gray-900">
                {formatCurrency(earning.amount)}
                <p className="text-[11px] text-gray-400">{(earning.commissionRate * 100).toFixed(0)}% rate</p>
            </td>
            <td className="px-4 py-4">
                <Badge
                    variant="outline"
                    className={`${STATUS_BADGE[earning.status]} px-3 py-1 rounded-lg font-medium capitalize`}
                >
                    {earning.status}
                </Badge>
            </td>
            <td className="px-4 py-4 text-xs text-gray-500">
                {new Date(dateLabel).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                })}
                {earning.status === 'voided' && earning.voidReason && (
                    <p className="text-[11px] text-gray-400 mt-0.5">{earning.voidReason}</p>
                )}
            </td>
        </tr>
    );
}
